import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchManifestDocument, fetchManifestDocumentCsv, fetchRoomingList, fetchRoomingListCsv, saveBlob, type CsvFile } from './api';
import type { ManifestDocument, RoomingList } from './types';

// A document is generated ON OPEN and never refetched behind the operator's back:
// the sheet they print is the sheet they saw. Closing and reopening regenerates.

export function useRoomingList(disruptionUrn: string | undefined, hotelRef: string | undefined, enabled = true) {
  return useQuery<RoomingList>({
    queryKey: ['disruptions', disruptionUrn, 'documents', 'rooming-list', hotelRef],
    queryFn: () => fetchRoomingList(disruptionUrn!, hotelRef!),
    enabled: !!disruptionUrn && !!hotelRef && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useManifestDocument(disruptionUrn: string | undefined, runUrn: string | undefined, enabled = true) {
  return useQuery<ManifestDocument>({
    queryKey: ['disruptions', disruptionUrn, 'documents', 'manifest', runUrn],
    queryFn: () => fetchManifestDocument(disruptionUrn!, runUrn!),
    enabled: !!disruptionUrn && !!runUrn && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** "Descargar CSV" — fetches the file and hands it to the browser. */
export function useCsvDownload(fetcher: () => Promise<CsvFile>) {
  return useMutation({
    mutationFn: async () => {
      const { blob, filename } = await fetcher();
      saveBlob(blob, filename);
      return filename;
    },
  });
}

export function useRoomingListDownload(disruptionUrn: string, hotelRef: string) {
  return useCsvDownload(() => fetchRoomingListCsv(disruptionUrn, hotelRef));
}

export function useManifestDownload(disruptionUrn: string, runUrn: string) {
  return useCsvDownload(() => fetchManifestDocumentCsv(disruptionUrn, runUrn));
}
